import { BLEND_MODES, Container, Graphics, SCALE_MODES, Sprite, Texture } from "pixi.js"
import { Coordinate, FirefliesConfig, TileSize } from "game-common/models"
import { ScalingParams } from "../../../../client_util"
import { nightFraction } from "./day_gate"

// One firefly. Plain numbers, mutated in place; nothing here allocates per frame.
interface Firefly {
    sprite: Sprite
    // The patch of air it works. A firefly does not travel -- it hovers over a spot, and this is the
    // spot; only `x`/`y` move, and never far from it.
    homeX: number
    homeY: number
    x: number
    y: number
    // Where in its little patch it is darting to next, as an offset from home, and when it will pick
    // somewhere else.
    targetX: number
    targetY: number
    nextDartMs: number
    // The flash. `phase` runs 0..1 over one whole cycle, of which only a short opening slice is lit.
    phase: number
    cycleRate: number
    litFraction: number
    scale: number
    peakAlpha: number
}

/**
 * Fireflies, after dark.
 *
 * THE FLASH IS THE ANIMAL. Everything else here is secondary: a firefly is a light that comes on, sits
 * there for a moment and goes out again, and between flashes it is not dim, it is GONE. So the pulse
 * spends most of its cycle at zero and the sprite is hidden outright while dark, which means only one
 * or two of them are ever actually on screen however many are about. That is what makes a handful read
 * as "the odd firefly" rather than as a swarm of fairy lights.
 *
 * THEY HOVER, THEY DO NOT TRAVEL. Each one keeps a home point and darts about within a tile or so of
 * it -- quick little moves with pauses between, so it reads as busy without going anywhere. The
 * butterflies are the opposite: they cross the clearing and settle. Two things that both flit would
 * have been one thing seen twice.
 *
 * WHERE IT DRAWS, and this is the one real decision. Every other ambience lives inside the world
 * surface, which is masked by the night visibility -- correct for leaves and fog, which you should
 * only see where you can see anything. A firefly is a LIGHT: the whole picture is one glowing in the
 * dark where you cannot otherwise see. So these go in the world OVERLAY surface, which the camera
 * moves but the darkness does not clip.
 *
 * That surface carries the camera translation without the world's 2x zoom (its own items multiply by
 * ScalingParams.surfaceZoom by hand), so this hangs a scaled container off it and everything inside
 * uses ordinary world coordinates like every other effect.
 *
 * Additive, because a glow is light rather than a pale sprite -- the same choice the campfire makes.
 *
 * LIVE TUNING, from the browser console -- no reload:
 *   window.fireflies = true | false     force them on in ANY room at any hour, or off in a dark one
 *   window.fireflyCount = 12            how many are about (only a fraction are lit at once)
 *   window.fireflyFlashSeconds = 2.5    how long one flash lasts
 */
export class Fireflies {
    readonly container: Container

    // Under the overlay surface's own items (chat bubbles, HUD), which sit at zero.
    static readonly Z_INDEX = -100

    static readonly MAX_FIREFLIES = 40

    private static readonly DEFAULT_COUNT = 5
    // How long one flash lasts, in seconds -- the difference between a firefly and a SPARK. The eye
    // needs long enough to find the thing, look at it, and watch it go out.
    //
    // A DURATION, not a share of the cycle. As a share it was the product of two separate random draws
    // (the share and the period), which spread it from one second to nearly three and moved it
    // whenever the cycle was retuned. How long a glow lasts and how often it comes round are two
    // different questions and are asked separately here.
    private static readonly DEFAULT_FLASH_SECONDS = 2.5
    // A flash cannot outlast its own cycle, however it is configured.
    private static readonly MAX_LIT_FRACTION = 0.75
    private static readonly DEFAULT_ALPHA = 0.95
    private static readonly DEFAULT_SCALE_MIN = 0.55
    private static readonly DEFAULT_SCALE_MAX = 0.95
    // Yellow-green, which is what the real ones are and what reads as a living light rather than a
    // lamp. The cooler one is the odd variation.
    private static readonly DEFAULT_COLORS = [0xd8ff8a, 0xc6ff70, 0xeaff9e, 0xa8ff86]

    // How far one strays from its home point.
    private static readonly HOVER_RADIUS = 0.85 * TileSize
    // ...and how quickly it gets there. Enough to look busy over a small patch.
    private static readonly DART_SPEED = 26

    private static readonly HALF_W = (ScalingParams.viewportX + 2) * TileSize
    private static readonly HALF_H = (ScalingParams.viewportY + 2) * TileSize
    // Beyond THIS it is re-homed. Deliberately outside the range it is placed in: a firefly homed near
    // the edge of the box and then hovering outward would otherwise trip the recycle and be flung to
    // the far side, every time it drifted.
    private static readonly RECYCLE_W = Fireflies.HALF_W + TileSize
    private static readonly RECYCLE_H = Fireflies.HALF_H + TileSize

    private static readonly TELEPORT_JUMP = Fireflies.HALF_W

    private flies: Firefly[] = []
    private activeCount = 0
    private texture: Texture | undefined
    private running = false
    private lastMs = 0
    private lastCenterX = 0
    private lastCenterY = 0
    private hasCenter = false

    constructor(private makeTexture: () => Texture) {
        const container = new Container()
        container.zIndex = Fireflies.Z_INDEX
        container.interactiveChildren = false
        container.visible = false
        // The overlay surface is camera-translated but NOT zoomed, so this container carries the zoom
        // and everything inside it is in plain world coordinates.
        container.scale.set(ScalingParams.surfaceZoom)
        this.container = container
    }

    // Smoothstep: eases in and out instead of ramping linearly, so neither end of a flash has a corner
    // on it.
    private static smooth = (t: number) => {
        const u = Math.min(1, Math.max(0, t))
        return u * u * (3 - 2 * u)
    }

    // Exposed for tests: how many are about, and how many are lit this instant.
    getActiveCount = () => (this.running ? this.activeCount : 0)
    getLitCount = () => (this.running ? this.flies.slice(0, this.activeCount).filter(f => f.sprite.visible).length : 0)

    update = (config: FirefliesConfig | null, center?: Coordinate, brightness?: number, sceneElevation = 0) => {
        const resolved = this.resolve(config, brightness)
        if (!resolved || !center) {
            this.stop()
            return
        }

        // They hover over the GROUND, which is drawn `sceneElevation` lower while the viewer stands on
        // an upper storey -- the camera re-bases by a storey's rise at a change of level so the world
        // holds still on screen (TranslationComputer.update). In the active floor's coordinates they
        // jumped that rise against the forest on every stair; in the ground's they stay over the same
        // patch of air, as the night fog does.
        this.container.y = sceneElevation * ScalingParams.surfaceZoom
        const cx = center.x, cy = center.y - sceneElevation

        const nowMs = performance.now()
        if (!this.running) {
            this.running = true
            this.container.visible = true
            this.lastMs = nowMs
            this.ensurePool(resolved.count, cx, cy, resolved, nowMs)
            this.scatter(cx, cy, resolved, nowMs)
            this.lastCenterX = cx
            this.lastCenterY = cy
            this.hasCenter = true
            return
        }

        this.ensurePool(resolved.count, cx, cy, resolved, nowMs)

        if (
            this.hasCenter &&
            (Math.abs(cx - this.lastCenterX) > Fireflies.TELEPORT_JUMP ||
                Math.abs(cy - this.lastCenterY) > Fireflies.TELEPORT_JUMP)
        ) {
            this.scatter(cx, cy, resolved, nowMs)
            this.lastMs = nowMs
            this.lastCenterX = cx
            this.lastCenterY = cy
            return
        }
        this.lastCenterX = cx
        this.lastCenterY = cy
        this.hasCenter = true

        let dt = (nowMs - this.lastMs) / 1000
        this.lastMs = nowMs
        if (!(dt > 0)) {
            return
        }
        dt = Math.min(dt, 0.05)

        this.step(dt, nowMs, cx, cy, resolved)
    }

    private stop = () => {
        if (!this.running) {
            return
        }
        this.running = false
        this.container.visible = false
        this.lastMs = 0
        this.hasCenter = false
    }

    private resolve = (config: FirefliesConfig | null, brightness?: number) => {
        const w = window as any
        const forced = typeof w.fireflies === "boolean" ? (w.fireflies as boolean) : undefined
        const enabled = forced !== undefined ? forced : !!config?.enabled
        if (!enabled) {
            return null
        }

        const num = (override: any, value: number | undefined, fallback: number) =>
            typeof override === "number" ? override : typeof value === "number" ? value : fallback

        // Forcing them on from the console lifts the night gate as well; there is no other way to look
        // at them in daylight.
        const nightOnly = forced === true ? false : config?.nightOnly !== false
        const night = nightOnly ? nightFraction(brightness) : 1
        if (night <= 0) {
            return null
        }

        const scaleMin = config?.scaleMin ?? Fireflies.DEFAULT_SCALE_MIN
        const scaleMax = config?.scaleMax ?? Fireflies.DEFAULT_SCALE_MAX

        return {
            // More of them the darker it gets, and none at all before dusk.
            count: Math.max(
                night > 0 ? 1 : 0,
                Math.min(
                    Fireflies.MAX_FIREFLIES,
                    Math.round(num(w.fireflyCount, config?.count, Fireflies.DEFAULT_COUNT) * night),
                ),
            ),
            flashSeconds: Math.max(
                0.1,
                num(w.fireflyFlashSeconds, config?.flashSeconds, Fireflies.DEFAULT_FLASH_SECONDS),
            ),
            alpha: Math.min(1, Math.max(0, num(w.fireflyAlpha, config?.alpha, Fireflies.DEFAULT_ALPHA))) * night,
            scaleMin: Math.max(0.05, Math.min(scaleMin, scaleMax)),
            scaleMax: Math.max(scaleMin, scaleMax),
            colors: config?.colors?.length ? config.colors : Fireflies.DEFAULT_COLORS,
        }
    }

    private step = (
        dt: number,
        nowMs: number,
        centerX: number,
        centerY: number,
        cfg: NonNullable<ReturnType<Fireflies["resolve"]>>,
    ) => {
        for (let i = 0; i < this.activeCount; i++) {
            const f = this.flies[i]

            // Wandered off the edge of what could be on screen -- re-home it on the other side. Doing
            // this only WHILE DARK is what makes it free: a firefly between flashes is not on screen,
            // so there is nothing to see move.
            if (
                !f.sprite.visible &&
                (Math.abs(f.homeX - centerX) > Fireflies.RECYCLE_W || Math.abs(f.homeY - centerY) > Fireflies.RECYCLE_H)
            ) {
                this.place(f, centerX, centerY, cfg, nowMs)
            }

            // Somewhere new in its patch to dart to, every so often. Short hops with pauses between
            // read as busy; one long drift reads as floating away.
            if (nowMs >= f.nextDartMs) {
                const angle = Math.random() * Math.PI * 2
                const reach = Math.random() * Fireflies.HOVER_RADIUS
                f.targetX = f.homeX + Math.cos(angle) * reach
                f.targetY = f.homeY + Math.sin(angle) * reach * 0.7
                f.nextDartMs = nowMs + 260 + Math.random() * 900
            }

            // Ease towards it, clamped so it cannot overshoot and jitter around the target.
            const dx = f.targetX - f.x
            const dy = f.targetY - f.y
            const distance = Math.hypot(dx, dy)
            if (distance > 0.01) {
                const stepLength = Math.min(distance, Fireflies.DART_SPEED * dt)
                f.x += (dx / distance) * stepLength
                f.y += (dy / distance) * stepLength
            }

            f.phase = (f.phase + f.cycleRate * dt) % 1

            // THE FLASH. Lit for the opening slice of the cycle and dark for all the rest -- and dark
            // means hidden, not faint: a firefly between flashes is gone, and a dim dot hanging there
            // would give away that it never left.
            const sprite = f.sprite
            if (f.phase < f.litFraction) {
                const u = f.phase / f.litFraction
                // Rise, HOLD, fade. The hold is the point: a bare rise-and-fall curve peaks for an
                // instant and is gone, which is what made these read as sparks however long the window
                // was. Coming up quickly, sitting there, and fading out slowly is what a firefly does.
                const glow = u < 0.22 ? Fireflies.smooth(u / 0.22) : u > 0.55 ? Fireflies.smooth((1 - u) / 0.45) : 1
                sprite.visible = true
                sprite.x = f.x
                sprite.y = f.y
                sprite.alpha = f.peakAlpha * cfg.alpha * glow
                // Swells very slightly as it brightens, which reads as light spilling rather than a
                // sprite fading up.
                sprite.scale.set(f.scale * (0.85 + 0.15 * glow))
            } else if (sprite.visible) {
                sprite.visible = false
            }
        }
    }

    private ensurePool = (
        count: number,
        centerX: number,
        centerY: number,
        cfg: NonNullable<ReturnType<Fireflies["resolve"]>>,
        nowMs: number,
    ) => {
        const target = Math.min(count, Fireflies.MAX_FIREFLIES)
        if (!this.texture && target > 0) {
            try {
                this.texture = this.makeTexture()
            } catch (e) {
                this.texture = Texture.WHITE
            }
        }
        while (this.flies.length < target) {
            const sprite = new Sprite(this.texture ?? Texture.WHITE)
            sprite.anchor.set(0.5)
            // A glow is light, not a pale sprite.
            sprite.blendMode = BLEND_MODES.ADD
            sprite.visible = false
            this.container.addChild(sprite)
            const fly: Firefly = {
                sprite,
                homeX: 0,
                homeY: 0,
                x: 0,
                y: 0,
                targetX: 0,
                targetY: 0,
                nextDartMs: 0,
                phase: 0,
                cycleRate: 0.3,
                litFraction: 0.2,
                scale: 0.7,
                peakAlpha: 1,
            }
            this.flies.push(fly)
            // Placed as it is created, not merely constructed -- the count rises as the light goes, so
            // this runs at dusk and not only on the first frame.
            this.place(fly, centerX, centerY, cfg, nowMs)
        }
        if (target === this.activeCount) {
            return
        }
        for (let i = 0; i < this.flies.length; i++) {
            if (i >= target) {
                this.flies[i].sprite.visible = false
            }
        }
        this.activeCount = target
    }

    private scatter = (
        centerX: number,
        centerY: number,
        cfg: NonNullable<ReturnType<Fireflies["resolve"]>>,
        nowMs: number,
    ) => {
        for (let i = 0; i < this.activeCount; i++) {
            this.place(this.flies[i], centerX, centerY, cfg, nowMs)
        }
    }

    // Give one firefly a fresh patch of air to work, and a fresh flash cycle.
    private place = (
        f: Firefly,
        centerX: number,
        centerY: number,
        cfg: NonNullable<ReturnType<Fireflies["resolve"]>>,
        nowMs: number,
    ) => {
        const rand = (min: number, max: number) => min + Math.random() * (max - min)
        f.homeX = centerX + (Math.random() * 2 - 1) * Fireflies.HALF_W
        f.homeY = centerY + (Math.random() * 2 - 1) * Fireflies.HALF_H
        f.x = f.homeX
        f.y = f.homeY
        f.targetX = f.homeX
        f.targetY = f.homeY
        f.nextDartMs = nowMs + Math.random() * 600
        // A random starting point in the cycle, so they do not all flash together the moment they are
        // placed. Real ones do synchronise; a whole camp doing it in step on the first frame after
        // dusk would read as a bug rather than as a phenomenon.
        f.phase = Math.random()
        // Seven to twelve seconds a cycle -- and this, not the lit fraction, is what decides how OFTEN
        // the camp flashes. Five of them on a ten-second cycle is a glow about every two seconds
        // somewhere in the trees, which is the "odd firefly" this is meant to be. On the two-to-five
        // seconds it had first, the same five read as a scattering of sparks going off constantly.
        f.cycleRate = rand(0.08, 0.14)
        // The lit share is DERIVED from the wanted duration and this one's own cycle, so the flash is
        // the length it was asked to be whatever period it happens to have drawn. The small variation
        // is on the duration, so no two are identical without any of them being the wrong length.
        const flashSeconds = cfg.flashSeconds * rand(0.85, 1.15)
        f.litFraction = Math.min(Fireflies.MAX_LIT_FRACTION, flashSeconds * f.cycleRate)
        f.scale = rand(cfg.scaleMin, cfg.scaleMax)
        f.peakAlpha = rand(0.8, 1)
        f.sprite.visible = false
        f.sprite.tint = cfg.colors[Math.floor(Math.random() * cfg.colors.length)]
        // Put the sprite where the firefly is, even though it is dark and nothing will look at it
        // until it flashes. A hidden sprite left at its old position is a stale reading waiting for
        // whatever asks next -- and `step` only writes a position on the frames it is lit.
        f.sprite.x = f.x
        f.sprite.y = f.y
    }

    /**
     * The glow, generated once: a small bright core inside a soft halo, drawn white so one texture
     * serves every tint. Stacked translucent circles rather than a blur filter -- a filter would want
     * a render target for something that is four rings of a Graphics.
     */
    static buildGlowTexture = (generate: (g: Graphics) => Texture): Texture => {
        const g = new Graphics()
        const RINGS = 7
        const OUTER = 9
        for (let i = RINGS; i >= 1; i--) {
            const radius = (OUTER * i) / RINGS
            // Falls off faster than linear so the core stays tight and the halo stays faint; a linear
            // ramp gives a soft ball with no centre to it.
            const alpha = 0.1 * Math.pow(1 - (i - 1) / RINGS, 1.6)
            g.beginFill(0xffffff, alpha)
            g.drawCircle(0, 0, radius)
            g.endFill()
        }
        // The core itself, near solid.
        g.beginFill(0xffffff, 0.95)
        g.drawCircle(0, 0, 1.4)
        g.endFill()
        const texture = generate(g)
        g.destroy()
        return texture
    }

    static textureFactory = (renderer: { generateTexture: (g: Graphics, options?: any) => Texture }) => (): Texture =>
        Fireflies.buildGlowTexture(g => renderer.generateTexture(g, { resolution: 2, scaleMode: SCALE_MODES.LINEAR }))
}
